import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { DeleteDialog } from 'common/components/deleteDialog/DeleteDialog';
import { setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { deleteUserGroup } from 'common/services/api/UserGroups/UserGroups.thunk';
import { DeleteDialogContentProps } from 'common/types/TypeDefs';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

export function UserGroupsDeleteDialogContent({ itemId = '', editingRow, title }: Readonly<DeleteDialogContentProps<{ name: string }>>) {
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const pageInfo = useAppSelector(selectPageInfo);
    const params = useParams();
    const tenantId = params.tenantId;
    const { name } = editingRow ?? {};

    const successHandler = (): void => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('User Group has been deleted.', { variant: 'success' });
    };

    const dispatch = useAppDispatch();
    const userGroupDeleteCallback = useCallback(() => {
        if (itemId) {
            setProcessing(true);
            dispatch(deleteUserGroup({
                id: itemId.toString(),
                tenantId: tenantId ?? ''
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setRows(result.rows));
                    successHandler();
                })
                .catch((error) => {
                    handleDeleteDialogError('User Group', enqueueSnackbar, extractStatusCode(error.message));
                })
                .finally(() => {
                    setProcessing(false);
                });
        }
    }, [itemId]);

    return (
        <DeleteDialog deleteCallback={userGroupDeleteCallback} processing={processing ?? false} title={title}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                User Group Name: <Typography component="span" color="text.primary" fontWeight="400">{name}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Tenant Name: <Typography component="span" color="text.primary" fontWeight="400">{pageInfo?.name}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
